import Button from '@/common/components/Button'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import { cn } from '@/common/utils/cn'
import { removeAccents } from '@/common/utils/removeAccents'
import { Node } from '@/features/privateCompanies/types/companyBranches.type'
import { useKeyPress, useReactFlow } from '@xyflow/react'
import { ChevronDownIcon, ChevronUpIcon, SearchIcon, XIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

interface SearchNodeProps {
  nodes: Node[]
  onSearchChange: (value: string) => void
  onMatchesChange: (ids: string[]) => void
  onActiveChange: (id: string | null) => void
}

export const SearchNode = ({
  nodes,
  onSearchChange,
  onMatchesChange,
  onActiveChange
}: SearchNodeProps) => {
  const ctrlFPressed = useKeyPress(['Control+F', 'Control+f'])
  const { setCenter } = useReactFlow()

  const [showSearchInput, setShowSearchInput] = useState(false)
  const [search, setSearch, _, forceNewSearch] = useDebounce('')
  const [nodesFiltered, setNodesFiltered] = useState<Node[]>([])
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0)

  const handlePrevious = () => {
    if (nodesFiltered.length === 0) {
      return
    }

    setCurrentSearchIndex((prevIndex) => (prevIndex === 0 ? nodesFiltered.length - 1 : prevIndex - 1))
  }

  const handleNext = () => {
    if (nodesFiltered.length === 0) {
      return
    }

    setCurrentSearchIndex((prevIndex) => (prevIndex + 1) % nodesFiltered.length)
  }

  const handleClose = () => {
    setShowSearchInput(false)
    forceNewSearch('')
    onSearchChange('')
    onMatchesChange([])
    onActiveChange(null)
  }

  const buildChildrenMap = (nodes: Node[]) => {
    const map = new Map<string | null, Node[]>()

    nodes.forEach((node) => {
      const parentId = node.data.parent_id ?? null

      if (!map.has(parentId)) {
        map.set(parentId, [])
      }

      map.get(parentId)!.push(node)
    })

    return map
  }

  const sortBranchsByParents = (nodes: Node[]) => {
    nodes.forEach(node => {
      node._createdAt = new Date(node.data.created_at).getTime()
    })
    const childrenMap = buildChildrenMap(nodes)
    for (const children of childrenMap.values()) {
      children.sort((a, b) => b._createdAt - a._createdAt)
    }

    const result: Node[] = []

    const appendChildrenInHierarchy = (parentId: string | null) => {
      const children = childrenMap.get(parentId) ?? []

      for (const child of children) {
        result.push(child)
        appendChildrenInHierarchy(child.data.id)
      }
    }

    appendChildrenInHierarchy(null)

    return result
  }

  useEffect(() => {
    setCurrentSearchIndex(0)
    onSearchChange(search)

    if (search.trim() === '') {
      setNodesFiltered([])
      onMatchesChange([])
      onActiveChange(null)
      return
    }

    const sortedByParent = sortBranchsByParents(nodes)

    const filtered =
      sortedByParent?.filter((node: any) => {
        const normalizedSearch = removeAccents(search.toLowerCase().trim())
        const normalizedDisplayName = removeAccents(node.data.display_name.toLowerCase().trim())

        return normalizedDisplayName.includes(normalizedSearch)
      }) || []

    setNodesFiltered(filtered)
    onMatchesChange(filtered.map((n) => n.id))
  }, [search, nodes?.length])

  useEffect(() => {
    if (nodesFiltered.length === 0) return

    const nodeToFocus = nodesFiltered[currentSearchIndex % nodesFiltered.length]

    if (!nodeToFocus) return

    onActiveChange(nodeToFocus.id)

    const x = nodeToFocus.position.x
    const y = nodeToFocus.position.y

    setCenter(x + 172, y + 40, { zoom: 1, duration: 500 })
  }, [nodesFiltered?.length, nodesFiltered?.[0]?.id, currentSearchIndex])

  useEffect(() => {
    if (ctrlFPressed) {
      setShowSearchInput((prev) => {
        if (prev) {
          forceNewSearch('')
        }

        return !prev
      })
    }
  }, [ctrlFPressed])

  if (!showSearchInput) {
    return (
      <Button
        variant="link-white"
        className="size-7 bg-white-200 rounded-full mr-1"
        onClick={() => setShowSearchInput(true)}
      >
        <SearchIcon className="size-4" />
      </Button>
    )
  }

  return (
    <div className="flex items-center gap-x-2 bg-white-200 rounded-md pr-1">
      <TextField
        autoFocus
        placeholder="Pesquisar"
        className="-mr-2"
        valueClassName="h-10 bg-transparent"
        onChange={(event) => setSearch((event.target as HTMLInputElement).value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()

            if (event.shiftKey) {
              handlePrevious()
            } else {
              handleNext()
            }
          }
          if (event.key === 'Escape') {
            handleClose()
          }
        }}
      />

      <p className={cn('text-xs font-mono', { 'opacity-0': !search.trim().length && nodesFiltered?.length === 0 })}>
        {nodesFiltered?.length > 0
          ? `${String(currentSearchIndex + 1).padStart(String(nodesFiltered?.length).length, '0')}/${
              nodesFiltered?.length
            }`
          : '0/0'}
      </p>

      <div className="h-6 w-px bg-white-200" />

      <Button
        variant="link-white"
        className="size-7 rounded-full hover:bg-white-200"
        onClick={handlePrevious}
        disabled={nodesFiltered?.length === 0}
      >
        <ChevronUpIcon className="size-5" />
      </Button>

      <Button
        variant="link-white"
        className="size-7 rounded-full hover:bg-white-200"
        onClick={handleNext}
        disabled={nodesFiltered?.length === 0}
      >
        <ChevronDownIcon className="size-5" />
      </Button>

      <Button variant="link-white" className="size-7 rounded-full hover:bg-white-200" onClick={handleClose}>
        <XIcon className="size-5" />
      </Button>
    </div>
  )
}
